import React, { useState } from "react";
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { COLORS } from "../../assets/theme";

export interface ICollapsibleSectionProps {
  title: React.ReactNode;
  icon?: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
  sx?: object;
}

const CollapsibleSection: React.FC<ICollapsibleSectionProps> = ({
  title,
  icon,
  defaultExpanded = true,
  expanded: expandedProp,
  onExpandedChange,
  headerExtra,
  children,
  sx,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const expanded = expandedProp ?? internalExpanded;

  const toggle = () => {
    const next = !expanded;
    if (onExpandedChange) {
      onExpandedChange(next);
    } else {
      setInternalExpanded(next);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: COLORS.iconColor,
        border: `1px solid ${COLORS.dividerColor}`,
        borderRadius: "12px",
        overflow: "hidden",
        ...sx,
      }}
    >
      <Box
        onClick={toggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          cursor: "pointer",
          userSelect: "none",
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1, sm: 1.25 },
          borderBottom: expanded ? `1px solid ${COLORS.dividerColor}` : "none",
          "&:hover": {
            backgroundColor: COLORS.goldButtonHoverBackground,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          {icon}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: COLORS.typographyColor }}
          >
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {headerExtra}
          <IconButton
            size="small"
            aria-label={expanded ? "Collapse" : "Expand"}
            sx={{ p: 0.5 }}
          >
            <KeyboardArrowDownIcon
              fontSize="small"
              sx={{
                color: COLORS.GOLD,
                transition: "transform 0.2s ease",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded} timeout="auto">
        {children}
      </Collapse>
    </Box>
  );
};

export default CollapsibleSection;
